import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Magnetic } from "@/components/fina/Magnetic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = [
  "Food production",
  "Pharmaceuticals",
  "Water & infrastructure",
  "Rail / transport",
  "Other",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    sector: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Enquiry received — we'll be in touch.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#050507] py-32 md:py-48 overflow-hidden"
    >
      {/* Dramatic radial darkening */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 40%, rgba(60,28,12,0.55) 0%, rgba(5,5,7,1) 65%)",
        }}
      />

      {/* Headline-finale moment */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center mb-24 md:mb-32">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="overline mb-6 inline-block"
        >
          — The brief
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="font-heading text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.03em] max-w-5xl mx-auto"
          data-testid="contact-finale"
        >
          Built for industries that
          <br />
          <span className="italic font-normal text-[#FF5722]">
            can&apos;t afford failure.
          </span>
        </motion.h2>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <p className="overline mb-6">— Start a project</p>
          <h2 className="font-heading text-white text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.05]">
            Tell us what
            <br />
            you're building.
          </h2>
          <p className="mt-8 text-white/55 font-body text-base leading-relaxed max-w-md">
            Drop a brief — we'll reply within one working day with a sensible
            next step, a quote, or a visit.
          </p>

          <div className="mt-12 grid grid-cols-1 gap-6 max-w-md">
            <InfoRow label="Workshop" value="Lincolnshire, United Kingdom" />
            <InfoRow label="Telephone" value="01427 615913" />
            <InfoRow label="Hours" value="Mon – Fri, 07:30 – 17:00" />
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7 bg-[#121214] border border-white/10 rounded-2xl p-8 md:p-12"
          data-testid="contact-form"
        >
          {done ? (
            <div className="flex flex-col items-start gap-5 py-16">
              <CheckCircle2 className="text-[#FF5722]" size={48} />
              <h3 className="font-heading text-white text-3xl tracking-tight">
                Thank you — enquiry received.
              </h3>
              <p className="text-white/60 font-body max-w-md">
                A member of the Fina team will be in touch shortly. If it's
                urgent, feel free to call us on 01427 615913.
              </p>
              <button
                type="button"
                data-testid="contact-reset"
                onClick={() => {
                  setDone(false);
                  setForm({
                    name: "",
                    email: "",
                    company: "",
                    sector: "",
                    message: "",
                  });
                }}
                className="mt-4 inline-flex items-center gap-2 text-sm text-white/70 hover:text-white"
              >
                Send another enquiry <ArrowRight size={14} />
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field
                label="Your name"
                value={form.name}
                onChange={onChange("name")}
                testid="contact-field-name"
                required
              />
              <Field
                label="Email"
                type="email"
                value={form.email}
                onChange={onChange("email")}
                testid="contact-field-email"
                required
              />
              <Field
                label="Company"
                value={form.company}
                onChange={onChange("company")}
                testid="contact-field-company"
              />
              <div>
                <label className="block text-xs uppercase tracking-[0.25em] text-white/50 mb-3">
                  Sector
                </label>
                <select
                  data-testid="contact-field-sector"
                  value={form.sector}
                  onChange={onChange("sector")}
                  className="w-full h-12 bg-transparent border-b border-white/15 focus:border-[#FF5722] text-white font-body outline-none transition-colors"
                >
                  <option value="" className="bg-[#121214]">
                    Select a sector
                  </option>
                  {SECTORS.map((s) => (
                    <option key={s} value={s} className="bg-[#121214]">
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-[0.25em] text-white/50 mb-3">
                  Project brief
                </label>
                <textarea
                  data-testid="contact-field-message"
                  rows={5}
                  value={form.message}
                  onChange={onChange("message")}
                  required
                  placeholder="Tell us about the scope, materials, timeline..."
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#FF5722] text-white font-body outline-none transition-colors pb-3 resize-none placeholder:text-white/30"
                />
              </div>

              <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-5 pt-4">
                <p className="text-xs text-white/40 font-body max-w-sm">
                  By submitting, you agree to be contacted by Fina about your
                  enquiry.
                </p>
                <Magnetic strength={0.25}>
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={loading}
                    className="cta-glow inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#FF5722] hover:bg-[#F4511E] text-black font-medium text-sm transition-colors disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Send enquiry <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </Magnetic>
              </div>
            </div>
          )}
        </motion.form>
      </div>
    </section>
  );
};

const Field = ({ label, type = "text", value, onChange, testid, required }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.25em] text-white/50 mb-3">
      {label}
      {required && <span className="text-[#FF5722] ml-1">*</span>}
    </label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full h-12 bg-transparent border-b border-white/15 focus:border-[#FF5722] text-white font-body outline-none transition-colors"
    />
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between border-b border-white/10 pb-3">
    <span className="text-xs uppercase tracking-[0.25em] text-white/40">
      {label}
    </span>
    <span className="font-body text-white/85 text-sm">{value}</span>
  </div>
);
